import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@dhis2/ui';
import { PrinterIcon, DownloadIcon, FileTextIcon } from 'lucide-react';
import PerformanceAppraisalPage from '../appraisal/PerformanceAppraisalPage';
import ReportLayout from './ReportLayout';
import AppraisalReport from '../appraisal/AppraisalReport';
import { exportAppraisalPDF } from '../reports/exportPDF';

const APPRAISAL_META = {
  'mid-year': {
    badge: 'Mid-Year Appraisal',
    title: 'Mid-Year Appraisal',
    description: 'View your self mid-year appraisal and print the current appraisal form as a PDF report.',
    programStage: 'xbEyVHbXlGK',
  },
  'year-end': {
    badge: 'Year-End Appraisal',
    title: 'Year-End Appraisal',
    description: 'View your self year-end appraisal and export the current appraisal form as a PDF report.',
    programStage: 'wEsUvf2luKr',
  },
};

export default function AppraisalReportPage({ 
  reportType,
  isTopLevelUser = false,  
  isPrintMode = false,
 }) {
  const meta = APPRAISAL_META[reportType] || APPRAISAL_META['mid-year'];
  const [showReportModal, setShowReportModal] = useState(false);
  const [appraisalData, setAppraisalData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const contentRef = useRef(null);

  // This will be called by the PerformanceAppraisalPage when data is loaded
  const handleAppraisalDataLoaded = (data) => {
    console.log('📊 Appraisal data loaded:', data);
    setAppraisalData(data);
    setIsContentReady(true);
  };

  // Ensure content is ready before export
  useEffect(() => {
    const checkContent = () => {
      const element = document.getElementById('appraisal-content');
      const objectivesElement = document.getElementById('my-objectives-content');
      
      if (element && element.children.length > 0) {
        setIsContentReady(true);
      }
      
      console.log('🔍 Content check:', {
        appraisal: !!element,
        objectives: !!objectivesElement,
        appraisalChildren: element?.children.length || 0,
        objectivesChildren: objectivesElement?.children.length || 0
      });
    };

    // Check immediately and after delays
    checkContent();
    const timer1 = setTimeout(checkContent, 1000);
    const timer2 = setTimeout(checkContent, 3000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const ensureAllContentExpanded = () => {
    // Check both content containers
    const containers = [
      document.getElementById('appraisal-content'),
      document.getElementById('my-objectives-content')
    ];
    
    containers.forEach((container, index) => {
      if (!container) {
        console.warn(`⚠️ Container ${index} not found`);
        return;
      }

      console.log(`🔍 Expanding content in container ${index}...`);
      
      // Find and expand all collapsed panels
      const collapsedPanels = container.querySelectorAll(
        '.apr-obj-panel--hidden, .objective-details, [class*="panel--hidden"], .collapsed'
      );
      collapsedPanels.forEach(panel => {
        panel.classList.remove('apr-obj-panel--hidden');
        panel.style.display = 'block';
        panel.style.maxHeight = 'none';
        panel.style.overflow = 'visible';
      });

      // Find all objective panels and make them visible
      const objPanels = container.querySelectorAll('.apr-obj-panel, .objective-panel');
      objPanels.forEach(panel => {
        panel.style.display = 'block';
        panel.style.maxHeight = 'none';
        panel.style.overflow = 'visible';
        panel.style.visibility = 'visible';
        panel.style.opacity = '1';
      });

      // Expand all content within objective panels
      const panelContent = container.querySelectorAll('.apr-obj-panel > div, .objective-details');
      panelContent.forEach(content => {
        content.style.display = 'block';
      });

      // Ensure all objective cards show details
      const objectiveCards = container.querySelectorAll('.objective-card, .apr-objective-card');
      objectiveCards.forEach(card => {
        const details = card.querySelector('.objective-details, .apr-obj-details');
        if (details) {
          details.style.display = 'block';
          details.style.maxHeight = 'none';
          details.style.overflow = 'visible';
        }
      });

      // Find all expandable buttons and click them
      const expandButtons = container.querySelectorAll('[class*="expand"], [class*="toggle"], [aria-expanded="false"]');
      expandButtons.forEach(button => {
        try {
          button.click();
        } catch (e) {
          button.setAttribute('aria-expanded', 'true');
        }
      });
    });
  };

 const handleExportPDF = async () => {
  setIsExporting(true);
  try {
    // Show loading indicator
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'flex';
    
    // Ensure content is ready
    if (!isContentReady) {
      console.warn('⚠️ Content not ready yet, waiting...');
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    // Ensure all content is expanded
    ensureAllContentExpanded();
    
    // Wait for DOM updates
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // ✅ ALWAYS use the full page container (which contains the logo)
    const elementId = 'report-shell__page';
    
    // ✅ Verify the element exists before exporting
    const element = document.getElementById(elementId);
    if (!element) {
      console.error('❌ Element not found:', elementId);
      const loadingElement = document.getElementById('pdf-loading');
      if (loadingElement) loadingElement.style.display = 'none';
      alert('Unable to generate PDF. Content not found. Please refresh and try again.');
      return;
    }
    
    console.log('✅ Found element:', elementId, 'with children:', element.children.length);
    
    // ✅ Export PDF using the correct element ID
    await exportAppraisalPDF(elementId, `${meta.title.replace(/\s/g, '-')}-report.pdf`);
    
    // Hide loading
    if (loadingElement) loadingElement.style.display = 'none';
  } catch (error) {
    console.error('❌ PDF export failed:', error);
    const loadingElement = document.getElementById('pdf-loading');
    if (loadingElement) loadingElement.style.display = 'none';
    alert(`Failed to generate PDF: ${error.message || 'Please try again.'}`);
  } finally {
    setIsExporting(false);
  }
};

  // Render the content with both containers
  const renderContent = () => (
    <div 
      style={{
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible'
      }}
    >
      {/* Appraisal Content */}
      <div 
        id="appraisal-content" 
        data-report-content="true"
        className="appraisal-content"
        ref={contentRef}
        style={{
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          overflow: 'visible'
        }}
      >
        <PerformanceAppraisalPage 
          reportType={reportType} 
          readOnlyReport={true}
          onDataLoaded={handleAppraisalDataLoaded}
        />
      </div>

      {/* My Objectives will be rendered inside PerformanceAppraisalPage */}
    </div>
  );

  return (
    <ReportLayout
      badge={meta.badge}
      title={meta.title}
      description={meta.description}
      hideAutoApproved={true}
      hideSelfRating={true}
      reportId="report-shell__page"
      isExporting={isExporting}
      onExportPDF={handleExportPDF}
      isTopLevelUser={isTopLevelUser}  
      isPrintMode={isPrintMode} 
    >
      {renderContent()}

      {/* Report Modal */}
      {showReportModal && appraisalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '1200px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <button
              onClick={() => setShowReportModal(false)}
              style={{
                position: 'sticky',
                top: '12px',
                right: '12px',
                float: 'right',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '18px',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '8px',
              }}
            >
              ✕
            </button>

            <AppraisalReport
              appraisalData={appraisalData}
              reportType={reportType}
              onClose={() => setShowReportModal(false)}
            />
          </div>
        </div>
      )}
    </ReportLayout>
  );
}